/**
 * LabelChips — a task's labels as small coloured chips (FRIDAY-105).
 *
 * `max` caps the chips and shows "+N" for the rest, for tight spots such as a
 * Kanban card. Without labels it shows `empty` (a dash by default).
 */

import type { TaskLabelInline } from '@friday/shared/realtime'
import { cn } from '@/lib/utils'

export function LabelChips({
  labels,
  max,
  empty = '—',
  className,
}: {
  labels: readonly TaskLabelInline[]
  max?: number
  empty?: React.ReactNode
  className?: string
}) {
  if (!labels.length) return <span className="text-surface-500">{empty}</span>
  const sorted = [...labels].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  const shown = max ? sorted.slice(0, max) : sorted
  const rest = sorted.length - shown.length
  return (
    <span className={cn('flex flex-wrap gap-1', className)}>
      {shown.map((l) => (
        <span
          key={l.id}
          className="max-w-[10rem] truncate rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: l.color + '25', color: l.color }}
        >
          {l.name}
        </span>
      ))}
      {rest > 0 && (
        <span className="text-surface-500 rounded-full px-1 py-0.5 text-[10px]" title={sorted.slice(shown.length).map((l) => l.name).join(', ')}>
          +{rest}
        </span>
      )}
    </span>
  )
}
